'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { googleMapsLoaderConfig } from '@/lib/googleMapsConfig';
import { geocodeAddress } from '@/actions/geocode';
import { buildGeocodeQuery } from '@/lib/listing-form';
import type { PinStatus } from '@/lib/listing-form';
import { listingFormStyles as s } from './listingFormStyles';
import { theme } from '@/lib/uiTheme';

type Props = {
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  pinStatus: PinStatus;
  onPinChange: (next: {
    latitude: number;
    longitude: number;
    pinStatus: PinStatus;
  }) => void;
  onPinStatusChange: (status: PinStatus, errorMessage?: string | null) => void;
  disabled?: boolean;
};

const mapContainerStyle = { width: '100%', height: '100%' };

function pinStatusLabel(status: PinStatus, errorMessage: string | null): string {
  switch (status) {
    case 'not_geocoded':
      return 'Pin not set. Geocode the address or place a pin on the map.';
    case 'geocoding':
      return 'Looking up address…';
    case 'geocoded':
      return 'Pin confirmed';
    case 'manually_adjusted':
      return 'Pin adjusted';
    case 'needs_reconfirm':
      return 'Address changed. Re-geocode or drag the pin to confirm.';
    case 'error':
      return errorMessage || 'Could not geocode this address. Adjust the address or place the pin manually.';
    default:
      return '';
  }
}

export default function ListingMapPin({
  address,
  city,
  state,
  zip,
  country,
  latitude,
  longitude,
  pinStatus,
  onPinChange,
  onPinStatusChange,
  disabled,
}: Props) {
  const { isLoaded, loadError } = useJsApiLoader(googleMapsLoaderConfig);
  const [localError, setLocalError] = useState<string | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const hasCoords = latitude != null && longitude != null && Number.isFinite(latitude) && Number.isFinite(longitude);

  const handleGeocode = useCallback(async () => {
    const query = buildGeocodeQuery({ address, city, state, zip, country });
    if (!address.trim() || !city.trim()) {
      setLocalError('Enter street address and city before geocoding.');
      onPinStatusChange('error', 'Enter street address and city before geocoding.');
      return;
    }
    setLocalError(null);
    onPinStatusChange('geocoding');
    const result = await geocodeAddress(query);
    if (!result) {
      const msg = 'Could not find that address. Check the details or place the pin manually.';
      setLocalError(msg);
      onPinStatusChange('error', msg);
      return;
    }
    onPinChange({
      latitude: result.lat,
      longitude: result.lon,
      pinStatus: 'geocoded',
    });
  }, [address, city, state, zip, country, onPinChange, onPinStatusChange]);

  useEffect(() => {
    if (hasCoords && mapRef.current) {
      mapRef.current.panTo({ lat: latitude!, lng: longitude! });
    }
  }, [hasCoords, latitude, longitude]);

  const center = hasCoords
    ? { lat: latitude!, lng: longitude! }
    : { lat: 39.8283, lng: -98.5795 };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleGeocode}
          disabled={disabled || pinStatus === 'geocoding'}
          style={{
            ...s.btnPrimary,
            ...(disabled || pinStatus === 'geocoding' ? s.btnDisabled : null),
          }}
        >
          {pinStatus === 'geocoding' ? 'Geocoding…' : hasCoords ? 'Re-geocode address' : 'Set pin from address'}
        </button>
        <span style={s.pinStatus}>{pinStatusLabel(pinStatus, localError)}</span>
      </div>

      {loadError ? (
        <p style={{ ...s.fieldError, marginTop: 12 }}>Map failed to load. You can still save coordinates after geocoding.</p>
      ) : null}

      <div style={s.mapFrame}>
        {!isLoaded ? (
          <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: theme.colors.inkSubtle }}>
            Loading map…
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={hasCoords ? 16 : 4}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            onClick={(e) => {
              if (disabled) return;
              const lat = e.latLng?.lat();
              const lng = e.latLng?.lng();
              if (lat == null || lng == null) return;
              onPinChange({ latitude: lat, longitude: lng, pinStatus: 'manually_adjusted' });
              setLocalError(null);
            }}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              clickableIcons: false,
            }}
          >
            {hasCoords ? (
              <Marker
                position={{ lat: latitude!, lng: longitude! }}
                draggable={!disabled}
                onDragEnd={(e) => {
                  const lat = e.latLng?.lat();
                  const lng = e.latLng?.lng();
                  if (lat == null || lng == null) return;
                  onPinChange({ latitude: lat, longitude: lng, pinStatus: 'manually_adjusted' });
                  setLocalError(null);
                }}
              />
            ) : null}
          </GoogleMap>
        )}
      </div>
      <p style={{ ...s.sectionHint, marginTop: 8, marginBottom: 0 }}>
        Drag the marker or tap the map to correct the pin. Coordinates are saved exactly as shown.
      </p>
    </div>
  );
}
